import { Component, OnInit } from '@angular/core';
import { FeedbackSurveyService } from '../services/survey-tools.service';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';
import { ReviewsService } from '../reviews.service';

@Component({
  selector: 'app-survey-tool',
  templateUrl: './survey-tool.component.html',
  styleUrls: ['./survey-tool.component.scss'],
})
export class SurveyToolComponent implements OnInit {
  private static readonly NON_DATA_COLUMN_KEYS = new Set([
    'responded_to',
    'internal_user_comment',
    'responded_by',
    'record_type',
  ]);
  private ngUnsubscribe$: Subject<any> = new Subject();
  private userLoginId: number;
  private accountId: number;
  private currentAccountIdent: string = '';
  private currentPageNumber: number;
  public currentUser: any;

  public actionItems = [
    {
      label: 'CSV',
      command: () => {
        this.exportCSV('csv');
      },
      disabled: false,
    },
    {
      label: 'XLSX',
      command: () => {
        this.exportXLSX('xlsx');
      },
      disabled: false,
    },
  ];
  // List of all feedback surveys.
  public feedbackSurveysList: any[] = [];
  // Main loading state of the page.
  public isLoadingData = false;
  public statisticsList: any[] = [];
  public displayColumns: Array<{ key: string; label: string; sortField: string | null }> = [];

  // Paginator properties
  public first: number = 0;
  public rows: number = 20;
  public totalRecords: number = 0;

  // Dialog properties
  public showEditDialog: boolean = false;
  public selectedSurvey: any = null;
  public editInternalComment: string = '';
  public editRespondedTo: boolean = false;
  public isSavingResponse: boolean = false;

  constructor(
    private feedbackSurveyService: FeedbackSurveyService,
    private sessionService: SessionService,
    private notifyService: NotifyService,
    private reviewsService: ReviewsService
  ) {}

  ngOnInit(): void {
    this.getCurrentAccountDetails();
    this.getCurrentUserDetails();
    // this.getFeedbackSurveysList();
  }

  /**
   * @description Subscribes to the selected account and stores identifiers used by API calls.
   * Keeps both numeric account ID and account ident in sync with session changes.
   * @returns None.
   */
  private getCurrentAccountDetails(): void {
    this.sessionService
      .getSelectedAccount$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((account) => {
        this.accountId = account?._id || null;
        this.currentAccountIdent = account?.ident || '';
      });
  }

  /**
   * @description Fetches survey records, normalizes response-edit fields, and derives dynamic table columns.
   * Column metadata is built from the raw API keys so each account can render its own schema while
   * preserving the fixed response-editing workflow in the last table column.
   * @param page Optional one-based page number sent to the survey API. Defaults to API behavior when omitted.
   * @param sortOrder Sorting direction for server-side ordering by date-like fields. Defaults to 'desc'.
   * @returns None. Updates list, statistics, pagination count, and loading state in place.
   * @throws None. API errors are handled in the error callback and surfaced with notifications.
   */
  private getFeedbackSurveysList(page?: number, sortOrder: string = 'desc'): void {
    this.isLoadingData = true;
    this.feedbackSurveyService
      .getFeedbackSurveysList(this.accountId, this.userLoginId, page, sortOrder)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (res) => {
          console.log('Raw data from getFeedbackSurveysList API:', res);
          const records = res?.records ?? res?.data?.records ?? [];
          this.displayColumns = this.buildDisplayColumns(records);
          if (records.length) {
            console.log('First raw record (keys from API):', records[0], 'Keys:', Object.keys(records[0]));
          }
          this.feedbackSurveysList = this.feedbackSurveyService.formatFeedbackSurveyData(records);
          console.log('Formatted feedbackSurveysList after processing:', this.feedbackSurveysList);
          this.totalRecords = res?.total ?? res?.data?.total ?? 0;
          this.statisticsList = res?.summary ?? res?.data?.summary ?? [];
          this.isLoadingData = false;
          this.notifyService.success('Feedback surveys fetched successfully.');
        },
        (err) => {
          this.isLoadingData = false;
          this.notifyService.info('Error while fetching feedback surveys.');
        },
        () => {
          this.isLoadingData = false;
        }
      );
  }

  /**
   * @description Method to export feedback surveys in CSV format.
   * @param format csv format to export feedback surveys.
   */
  exportCSV(format: string): void {
    this.reviewsService.genCSVReport(this.feedbackSurveysList, format, 'feedback-survey');
  }

  /**
   * @description Exports the current survey list in XLSX format using runtime row keys as headers.
   * No-op when there are no records to export.
   * @param format XLSX export format identifier expected by the review export utility.
   * @returns None.
   * @throws None. Any export utility failures bubble from downstream services.
   */
  exportXLSX(format: string): void {
    if (!this.feedbackSurveysList.length) {
      this.notifyService.info('No survey records available to export.');
      return;
    }

    const headers = Object.keys(this.feedbackSurveysList[0]);
    const tableData = this.feedbackSurveysList.map((survey) => {
      return Object.values(survey);
    });
    const xlslDataArray = [headers, ...tableData];
    this.reviewsService.exportAsExcelFile(xlslDataArray, format);
  }

  /**
   * @description Method to handle page change event from paginator.
   * @param event Page change event from paginator
   */
  public pageChange(event): void {
    this.first = event.first;
    this.rows = event.rows;
  }

  public lazyLoadLocations(event) {
    let sortOrder: string = 'desc';
    if (!event.multiSortMeta) {
      sortOrder = 'desc';
    } else {
      if (event.multiSortMeta[0].order === 1) {
        sortOrder = 'asc';
      } else if (event.multiSortMeta[0].order === -1) {
        sortOrder = 'desc';
      } else {
        sortOrder = 'desc';
      }
    }
    this.currentPageNumber = event.first / 25 + 1;
    this.getFeedbackSurveysList(this.currentPageNumber, sortOrder);
  }

  /**
   * @description Builds ordered column metadata from raw API records for dynamic table rendering.
   * The method keeps first-seen key order, skips response-edit helper keys, and marks date-like columns
   * as sortable with server-recognized fields.
   * @param records Raw survey records returned by the survey results endpoint.
   * @returns Ordered column metadata array. Returns an empty array when records are not present.
   * @throws None.
   */
  private buildDisplayColumns(records: any[]): Array<{ key: string; label: string; sortField: string | null }> {
    if (!Array.isArray(records) || !records.length) {
      return [];
    }

    const orderedKeys = records.reduce((keys: string[], record: Record<string, any>) => {
      Object.keys(record || {}).forEach((key) => {
        if (!SurveyToolComponent.NON_DATA_COLUMN_KEYS.has(key) && !keys.includes(key)) {
          keys.push(key);
        }
      });
      return keys;
    }, []);

    return orderedKeys.map((key) => ({
      key,
      label: this.formatColumnLabel(key),
      sortField: this.getSortFieldForColumn(key),
    }));
  }

  /**
   * @description Converts a raw API key into a human-readable table header label.
   * The conversion expands snake_case/camelCase keys and uppercases known abbreviations.
   * @param key Raw server key used in survey records.
   * @returns Readable header label for the table column.
   * @throws None.
   */
  private formatColumnLabel(key: string): string {
    if (!key) {
      return '';
    }

    const expanded = key
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .trim();

    return expanded
      .split(/\s+/)
      .map((word) => {
        if (word.toUpperCase() === 'ID') {
          return 'ID';
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  /**
   * @description Resolves the sortable backend field for a given dynamic column key.
   * Date-like keys map to API-supported sort fields; all other keys return null and remain unsorted.
   * @param key Raw server key used in survey records.
   * @returns Sort field value for PrimeNG sort directives, or null when sorting is unavailable.
   * @throws None.
   */
  private getSortFieldForColumn(key: string): string | null {
    if (!key) {
      return null;
    }

    const normalizedKey = key.toLowerCase();
    if (normalizedKey === 'date' || normalizedKey === 'updated_at' || normalizedKey === 'updatedat') {
      return 'updated_at';
    }

    return null;
  }

  /**
   * @description Returns a display-safe cell value for dynamic data columns.
   * Nullish and empty-string values render as 'N/A', arrays are comma-joined, and objects are stringified.
   * @param record Survey row object rendered in the table body.
   * @param key Column key resolved from server record schema.
   * @returns Primitive display value suitable for interpolation in table cells.
   * @throws None.
   */
  public getDisplayCellValue(record: Record<string, any>, key: string): string | number | boolean {
    const value = record?.[key];

    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'string' && value.trim() === '') {
      return 'N/A';
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : 'N/A';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value;
  }

  /**
   * @description: Get the current user details.
   * @returns: void
   * @arguments: void
   */
  private getCurrentUserDetails(): void {
    this.sessionService
      .getCurrentUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((user) => {
        this.userLoginId = user?.login?._id;
        this.currentUser = user; // Store user for "Responded by" field
      });
  }

  /**
   * @description Open edit dialog for response fields
   * @param survey Survey data to edit
   */
  public openEditDialog(survey: any): void {
    this.selectedSurvey = survey;
    this.editInternalComment = survey?.internal_user_comment || '';
    this.editRespondedTo = survey?.responded_to ?? false;
    this.showEditDialog = true;
  }

  /**
   * @description Close edit dialog without saving
   */
  public closeEditDialog(): void {
    this.showEditDialog = false;
    this.selectedSurvey = null;
    this.editInternalComment = '';
    this.editRespondedTo = false;
  }

  /**
   * @description Build "Responded by" object for API: { user_id, name, email_id }.
   */
  public getRespondedByValue(): { user_id: number; name: string; email_id: string } | null {
    if (!this.currentUser?.login) {
      return null;
    }
    const user_id = this.currentUser.login._id;
    const name = this.currentUser.login.note ?? this.currentUser.login.name ?? '';
    const email_id = this.currentUser.login.loginId ?? this.currentUser.email_id ?? '';
    return { user_id, name, email_id };
  }

  /**
   * @description Display string for "Responded by" in UI (e.g. "rory (rory@renderseo.com)").
   */
  public getRespondedByDisplayText(): string {
    const obj = this.getRespondedByValue();
    if (!obj) {
      return 'N/A';
    }
    return obj.email_id ? `${obj.name} (${obj.email_id})` : obj.name || 'N/A';
  }

  /**
   * @description Saves edited response fields via the survey update API.
   * Resolves account ident from the selected row first, then falls back to the current
   * session account ident when row data is missing the identifier.
   * @returns None. Returns early when no row is selected or required payload fields are missing.
   */
  public saveResponseFields(): void {
    if (!this.selectedSurvey) {
      return;
    }
    const accountIdent = this.selectedSurvey.account_ident || this.currentAccountIdent;
    const type = this.selectedSurvey.record_type;
    if (!accountIdent || !type || type === 'N/A') {
      this.notifyService.info('Missing account or response type; cannot update.');
      return;
    }
    const respondedBy = this.getRespondedByValue();
    const body = {
      account_ident: accountIdent,
      type,
      updates: {
        responded_to: this.editRespondedTo,
        internal_user_comment: this.editInternalComment,
        ...(respondedBy && { responded_by: respondedBy }),
      },
    };
    console.log('Survey update API request body:', body);
    this.isSavingResponse = true;
    this.feedbackSurveyService
      .updateSurveyResponse(this.accountId, this.userLoginId, body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        () => {
          this.selectedSurvey.responded_to = this.editRespondedTo;
          this.selectedSurvey.internal_user_comment = this.editInternalComment;
          this.selectedSurvey.responded_by = this.getRespondedByDisplayText();
          this.notifyService.success('Response updated successfully.');
          this.closeEditDialog();
        },
        () => {
          this.notifyService.info('Error while updating response.');
        },
        () => {
          this.isSavingResponse = false;
        }
      );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
